/**
 * ChatsByCountryMap Component
 * List of chats by country.
 *
 * The server now returns a bounded list (top 10 + an "Other" rollup) with
 * server-computed percentages — this component renders what it receives and
 * never re-sorts or re-slices (the "Other" row must stay last).
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material';
import { ExpandMore, ExpandLess, Public } from '@mui/icons-material';
import { CountryStats } from '@/hooks/useAnalytics';

interface ChatsByCountryMapProps {
  countries: CountryStats[];
  loading: boolean;
}

const ChatsByCountryMap: React.FC<ChatsByCountryMapProps> = ({ countries, loading }) => {
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Server order preserved (sorted desc, "Other" last). Card shows the first 5;
  // the rest (at most 6 rows incl. Other) sit behind the expander/dialog.
  const topCountries = countries.slice(0, 5);
  const remainingCountries = countries.slice(5);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Chats by Country
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <Typography color="text.secondary">Loading country data...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!countries || countries.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Chats by Country
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <Typography color="text.secondary">No country data available</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Chats by Country</Typography>
            <IconButton
              size="small"
              onClick={() => setDialogOpen(true)}
              sx={{ color: 'primary.main' }}
            >
              <Public />
            </IconButton>
          </Box>

          {/* Country list with server-computed percentage bars */}
          <Box mb={2}>
            {topCountries.map((country) => (
              <Box key={country.countryCode} mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" fontWeight="medium">
                    {country.countryName} ({country.countryCode})
                  </Typography>
                  <Chip label={country.conversations} size="small" color="primary" />
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 8,
                    backgroundColor: 'grey.200',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${country.percentagePct}%`,
                      height: '100%',
                      backgroundColor: 'primary.main',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>

          {remainingCountries.length > 0 && (
            <>
              <Button
                size="small"
                onClick={() => setExpanded(!expanded)}
                endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                sx={{ mt: 1 }}
              >
                {expanded ? 'Show Less' : `Show ${remainingCountries.length} More`}
              </Button>

              {expanded && (
                <List dense sx={{ mt: 1 }}>
                  {remainingCountries.map((country) => (
                    <ListItem key={country.countryCode}>
                      <ListItemText
                        primary={`${country.countryName} (${country.countryCode})`}
                        secondary={`${country.conversations} conversations · ${country.percentagePct}%`}
                      />
                      <ListItemSecondaryAction>
                        <Chip label={country.conversations} size="small" />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Full List Dialog — bounded server list (max 11 rows incl. Other) */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Chats by Country</DialogTitle>
        <DialogContent>
          <List>
            {countries.map((country) => (
              <ListItem key={country.countryCode}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1" fontWeight="medium">
                        {country.countryName}
                      </Typography>
                      {country.countryCode !== 'OTHER' && (
                        <Typography variant="body2" color="text.secondary">
                          ({country.countryCode})
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box mt={0.5}>
                      <Box
                        sx={{
                          width: '100%',
                          height: 6,
                          backgroundColor: 'grey.200',
                          borderRadius: 1,
                          overflow: 'hidden',
                          mb: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: `${country.percentagePct}%`,
                            height: '100%',
                            backgroundColor: 'primary.main',
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {country.percentagePct}% of total conversations
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Chip label={country.conversations} color="primary" />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChatsByCountryMap;
